import { FC, useState } from "react"
import { Button, Card } from "react-bootstrap"
import { useTranslation } from "react-i18next"
import {useTheme} from "../../context/ThemeProvider";

export interface ExternalContentProps {
    src: string
    title?: string
    description?: string
    height?: number | string
    link?: string
    linkText?: string
}

const ExternalContent: FC<ExternalContentProps> = ({
                                                       src,
                                                       title,
                                                       description,
                                                       height = 450,
                                                       link,
                                                       linkText,
                                                   }) => {
    const { t } = useTranslation("site")
    const theme = useTheme()
    const isDark = theme.getTheme() === "dark"

    const [loaded, setLoaded] = useState(false)

    const resolvedTitle = title ?? t("externalContent.title")
    const resolvedDescription =
        description ?? t("externalContent.description")
    const resolvedLinkText =
        linkText ?? t("externalContent.openInNewWindow")

    return (
        <div className="position-relative">
            {loaded ? (
                <>
                    <iframe
                        title={resolvedTitle}
                        src={src}
                        width="100%"
                        height={height}
                        loading="lazy"
                        referrerPolicy="strict-origin-when-cross-origin"
                        style={{
                            border: "1px solid var(--bs-border-color)",
                            borderRadius: "0.5rem",
                            backgroundColor: isDark ? "#212529" : "#ffffff",
                        }}
                    />

                    {link && (
                        <Button
                            href={link}
                            target="_blank"
                            rel="noopener noreferrer"
                            variant={isDark ? "light" : "primary"}
                            className="position-absolute bottom-0 end-0 m-3"
                        >
                            <i className="bi bi-box-arrow-up-right"></i>{" "}
                            {resolvedLinkText}
                        </Button>
                    )}
                </>
            ) : (
                <Card
                    className={
                        isDark
                            ? "text-center bg-dark text-light"
                            : "text-center bg-light text-dark"
                    }
                    style={{
                        height,
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        border: "1px solid var(--bs-border-color)",
                    }}
                >
                    <Card.Body style={{ maxWidth: 650 }}>
                        <div
                            style={{
                                fontSize: "3rem",
                                marginBottom: "1rem",
                            }}
                        >
                            🌍
                        </div>

                        <Card.Title>{resolvedTitle}</Card.Title>

                        <Card.Text
                            className={
                                isDark
                                    ? "text-light opacity-75"
                                    : "text-muted"
                            }
                        >
                            {resolvedDescription}
                        </Card.Text>

                        <Button
                            variant="primary"
                            onClick={() => setLoaded(true)}
                        >
                            {t("externalContent.load")}
                        </Button>

                        <div
                            className={
                                isDark
                                    ? "mt-3 text-light opacity-75"
                                    : "mt-3 text-muted"
                            }
                            style={{ fontSize: ".8rem" }}
                        >
                            {t("externalContent.privacyNotice")}
                        </div>
                    </Card.Body>
                </Card>
            )}
        </div>
    )
}

export default ExternalContent